import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Plus, Trash2 } from "lucide-react";
import type { PickupLocation } from "@gaole/shared";
import { MY_STATE_LABELS, parsePickupLocationsDraft } from "@gaole/shared";
import { Button, Input, Select, Textarea, Toggle } from "../../components/ui";
import { PickupAvailabilityEditor } from "./PickupAvailabilityEditor";

function newLocationId() {
  return globalThis.crypto?.randomUUID?.() ?? `loc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyLocation(): PickupLocation {
  return {
    id: newLocationId(),
    name: "",
    address1: "",
    city: "",
    state: MY_STATE_LABELS[0] ?? "Selangor",
    postcode: "",
    country: "MY",
    active: true,
  };
}

export function PickupLocationsEditor({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (locations: PickupLocation[]) => void;
}) {
  const locations = useMemo(() => parsePickupLocationsDraft(value), [value]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [focusNameForId, setFocusNameForId] = useState<string | null>(null);

  useEffect(() => {
    if (!focusNameForId || selectedId !== focusNameForId) return;
    nameInputRef.current?.focus();
    setFocusNameForId(null);
  }, [focusNameForId, selectedId]);

  const selected =
    locations.find((l) => l.id === selectedId) ?? locations[locations.length - 1] ?? null;

  function updateLocation(id: string, patch: Partial<PickupLocation>) {
    onChange(locations.map((loc) => (loc.id === id ? { ...loc, ...patch } : loc)));
  }

  function addLocation() {
    const loc = emptyLocation();
    onChange([...locations, loc]);
    setSelectedId(loc.id);
    setFocusNameForId(loc.id);
  }

  function removeLocation(id: string) {
    const next = locations.filter((l) => l.id !== id);
    onChange(next);
    if (selectedId === id) setSelectedId(next[0]?.id ?? null);
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted">
        Customers choose a location and preferred pickup date/time at checkout. Pickup is always free.
      </p>

      <div className="flex flex-wrap gap-2">
        {locations.map((loc) => (
          <button
            key={loc.id}
            type="button"
            onClick={() => setSelectedId(loc.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              selected?.id === loc.id
                ? "border-brand-pink/50 bg-brand-pink/10 text-brand-pink"
                : "border-line bg-surface-2 text-muted hover:text-foreground"
            }`}
          >
            <MapPin size={12} />
            {loc.name.trim() || "Untitled location"}
            {!loc.active && <span className="text-[10px] text-muted">(inactive)</span>}
          </button>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={addLocation}>
          <Plus size={14} className="mr-1" />
          Add
        </Button>
      </div>

      {selected ? (
        <div className="space-y-3 rounded-xl border border-line bg-surface-2/40 p-4">
          <div className="flex items-start justify-between gap-3">
            <Input
              ref={nameInputRef}
              label="Location name"
              placeholder="e.g. Main store"
              value={selected.name}
              onChange={(e) => updateLocation(selected.id, { name: e.target.value })}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-6 shrink-0 text-red-400"
              onClick={() => removeLocation(selected.id)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
          <Input
            label="Address line 1"
            value={selected.address1}
            onChange={(e) => updateLocation(selected.id, { address1: e.target.value })}
          />
          <Input
            label="Address line 2"
            value={selected.address2 ?? ""}
            onChange={(e) => updateLocation(selected.id, { address2: e.target.value || undefined })}
          />
          <div className="form-row-2">
            <Input
              label="Postcode"
              value={selected.postcode}
              onChange={(e) => updateLocation(selected.id, { postcode: e.target.value })}
            />
            <Input
              label="City"
              value={selected.city}
              onChange={(e) => updateLocation(selected.id, { city: e.target.value })}
            />
          </div>
          <Select
            label="State"
            value={selected.state}
            onChange={(e) => updateLocation(selected.id, { state: e.target.value })}
          >
            {MY_STATE_LABELS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          <Input
            label="Phone"
            value={selected.phone ?? ""}
            onChange={(e) => updateLocation(selected.id, { phone: e.target.value || undefined })}
          />
          <Textarea
            label="Collection instructions"
            rows={2}
            hint="Shown to customers after they select this location"
            value={selected.instructions ?? ""}
            onChange={(e) => updateLocation(selected.id, { instructions: e.target.value || undefined })}
          />
          <PickupAvailabilityEditor
            value={selected.availability}
            onChange={(availability) => updateLocation(selected.id, { availability })}
          />
          <Input
            label="Waze link"
            placeholder="https://waze.com/ul/…"
            hint="Optional — opens in Waze when customers tap it"
            value={selected.wazeUrl ?? ""}
            onChange={(e) => updateLocation(selected.id, { wazeUrl: e.target.value || undefined })}
          />
          <Input
            label="Google Maps link"
            placeholder="https://maps.google.com/…"
            hint="Optional — opens in Google Maps when customers tap it"
            value={selected.googleMapsUrl ?? ""}
            onChange={(e) => updateLocation(selected.id, { googleMapsUrl: e.target.value || undefined })}
          />
          <Toggle
            label="Active"
            checked={selected.active}
            onChange={(v) => updateLocation(selected.id, { active: v })}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
          Add a pickup location to enable in-store collection.
        </div>
      )}
    </div>
  );
}
